import { AxiosError } from 'axios'
import { useCallback } from 'react'

import { useUser } from '../contexts/UserContext'
import apiService from '../services/Api'
import { TContestLinkType } from '../services/Contests/interfaces'
import { Event } from '../services/Event/types'
import { Teacher } from '../services/Teacher/types'

export { getApiErrorMessage } from './apiError'
export { useAppToast } from './useAppToast'
export type { IAppToast } from './useAppToast'
export { COPIED_RESET_MS, useCopyToClipboard } from './useCopyToClipboard'
export type { ICopyOptions, ICopyToClipboard } from './useCopyToClipboard'
export { APP_TITLE, useDocumentTitle } from './useDocumentTitle'
export { useDownload } from './useDownload'
export type { IDownload } from './useDownload'
export { usePendingRows } from './usePendingRows'
export type { IPendingRows } from './usePendingRows'
export { EMAIL_REGEX, hasFullName } from './validation'

const sanitizeDownloadFilename = (filename: string): string => {
  const sanitized = filename
    .replace(/^["']|["']$/g, '')
    .replace(/[/\\]+/g, '_')
    .replace(/[\u0000-\u001f\u007f]+/g, '')
    .trim()

  return sanitized || 'download'
}

const decodeContentDispositionFilename = (value: string): string => {
  try {
    return decodeURIComponent(value.replace(/\+/g, '%20'))
  } catch {
    return value
  }
}

const extractDownloadFilename = (disposition: string, fallbackFilename: string): string => {
  const filenameStarMatch = disposition.match(/filename\*\s*=\s*([^;]+)/i)

  if (filenameStarMatch) {
    const rawValue = filenameStarMatch[1].trim().replace(/^"(.*)"$/, '$1')
    const rfc5987Match = rawValue.match(/^([^']*)'[^']*'(.*)$/)
    const encodedFilename = rfc5987Match ? rfc5987Match[2] : rawValue
    const decodedFilename = decodeContentDispositionFilename(encodedFilename)
    const sanitizedFilename = sanitizeDownloadFilename(decodedFilename)

    if (sanitizedFilename) {
      return sanitizedFilename
    }
  }

  const filenameMatch = disposition.match(/filename\s*=\s*([^;]+)/i)

  if (filenameMatch) {
    const rawValue = filenameMatch[1].trim().replace(/^"(.*)"$/, '$1')
    const decodedFilename = decodeContentDispositionFilename(rawValue)
    const sanitizedFilename = sanitizeDownloadFilename(decodedFilename)

    if (sanitizedFilename) {
      return sanitizedFilename
    }
  }

  return sanitizeDownloadFilename(fallbackFilename)
}

/**
 * Desembrulha a recusa de um download.
 *
 * Com `responseType: 'blob'` o axios entrega **também o corpo de erro** como Blob, então
 * `response.data.message` não existe e o `getApiErrorMessage` devolvia `undefined` para todo
 * download do painel — a mensagem que o backend mandou (o teto de linhas da exportação, por
 * exemplo) nunca chegava a quem clicou. As quatro telas de exportação respondiam com uma frase
 * fixa, e só o `SurveyReportPage` lia o Blob, com uma função local que só olhava `message`.
 *
 * Lendo o corpo aqui, o erro que sai destas três funções tem o mesmo formato de qualquer outro
 * da API, e `toast.error(titulo, error)` volta a funcionar sem nada no call site.
 */
const unwrapBlobError = async (error: unknown): Promise<unknown> => {
  const response = (error as AxiosError)?.response

  if (!(response?.data instanceof Blob)) {
    return error
  }

  try {
    response.data = JSON.parse(await response.data.text())
  } catch {
    // Corpo não-JSON (uma página de erro do servidor, um proxy): fica como veio, e o
    // `getApiErrorMessage` devolve `undefined` para quem chamou ficar com o próprio título.
  }

  return error
}

const getBlobResponse = async (url: string) => {
  try {
    return await apiService.get(url, { responseType: 'blob' })
  } catch (error) {
    throw await unwrapBlobError(error)
  }
}

const postBlobResponse = async (url: string, data: unknown) => {
  try {
    return await apiService.post(url, data, { responseType: 'blob' })
  } catch (error) {
    throw await unwrapBlobError(error)
  }
}

export const downloadBlobFromApi = (url: string, fallbackFilename: string): Promise<void> => {
  return getBlobResponse(url).then((response) => {
    const objectUrl = window.URL.createObjectURL(response.data)
    const disposition: string = response.headers['content-disposition'] ?? ''
    const filename = extractDownloadFilename(disposition, fallbackFilename)
    const link = document.createElement('a')
    link.href = objectUrl
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.parentNode?.removeChild(link)
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0)
  })
}

const openBlobInNewTab = (data: unknown, fallbackContentType: string): void => {
  const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: fallbackContentType })
  const objectUrl = window.URL.createObjectURL(blob)
  window.open(objectUrl, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000)
}

export const openBlobFromApi = (url: string, fallbackContentType = 'application/pdf'): Promise<void> => {
  return getBlobResponse(url).then((response) => openBlobInNewTab(response.data, fallbackContentType))
}

/**
 * O `openBlobFromApi` para o arquivo que só sai de um POST — a pré-visualização que leva no corpo
 * o que ainda não foi salvo (o certificado de evento manda o modelo e o layout em edição).
 */
export const openPostedBlobFromApi = (
  url: string,
  data: unknown,
  fallbackContentType = 'application/pdf'
): Promise<void> => {
  return postBlobResponse(url, data).then((response) => openBlobInNewTab(response.data, fallbackContentType))
}

export const printBlobFromApi = (url: string, fallbackContentType = 'application/pdf'): Promise<void> => {
  return getBlobResponse(url).then((response) => {
    const blob =
      response.data instanceof Blob ? response.data : new Blob([response.data], { type: fallbackContentType })
    const objectUrl = window.URL.createObjectURL(blob)

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'

    let printed = false
    let cleanedUp = false

    const cleanup = () => {
      if (cleanedUp) return
      cleanedUp = true
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
      window.URL.revokeObjectURL(objectUrl)
    }

    const fallbackOpenInNewTab = () => {
      if (printed) return
      printed = true
      const newWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer')
      if (!newWindow) {
        const link = document.createElement('a')
        link.href = objectUrl
        link.setAttribute('download', 'identificador.pdf')
        document.body.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
      }
      window.setTimeout(cleanup, 2000)
    }

    // Se o viewer de PDF estiver bloqueado por policy corporativa,
    // o iframe não carrega — abre em nova aba como fallback.
    const loadTimeout = window.setTimeout(fallbackOpenInNewTab, 3000)

    iframe.onload = () => {
      window.clearTimeout(loadTimeout)
      try {
        const win = iframe.contentWindow
        if (!win) {
          fallbackOpenInNewTab()
          return
        }
        win.focus()
        win.print()
        printed = true
        window.setTimeout(cleanup, 60000)
      } catch {
        fallbackOpenInNewTab()
      }
    }

    iframe.src = objectUrl
    document.body.appendChild(iframe)
  })
}

export const formatDate = (date: string) => {
  const d = new Date(date)

  return d.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

// Formats a 'YYYY-MM-DD' string as 'DD/MM/YYYY' without timezone drift.
// Prefer this over formatDate for date-only values (formatDate parses via Date, which
// shifts to the previous day in negative-UTC timezones like America/Sao_Paulo).
export const formatIsoDate = (date: string) => date.split('-').reverse().join('/')

// Formats a contest's artwork withdrawal window as 'DD/MM/YYYY a DD/MM/YYYY'.
// Falls back to the single date that is filled in, or to an empty string.
/**
 * Formata a data que a API devolve, aceitando tanto `'2026-09-09'` quanto o
 * `'2026-09-09T00:00:00.000000Z'` que o Laravel manda nos casts de data.
 *
 * Use isto, e não `formatDate`, para valores que são só data. O painel tinha 14 sites
 * repetindo `formatDate(x.split('T')[0] + 'T00:00:00')` à mão — o `T00:00:00` existia só
 * para impedir que o `Date` interpretasse a string como UTC e voltasse um dia em
 * America/Sao_Paulo. Aqui nem há `Date` envolvido, então o drift não é possível.
 */
export const formatApiDate = (date?: string | null): string => {
  if (!date) return ''

  return formatIsoDate(date.split('T')[0])
}

export const formatWithdrawWindow = (start?: string | null, end?: string | null) => {
  const from = start ? formatIsoDate(start.split('T')[0]) : ''
  const to = end ? formatIsoDate(end.split('T')[0]) : ''

  if (from && to) return `${from} a ${to}`

  return from || to
}

/**
 * Lê o instante que a API devolve.
 *
 * **Uma data sem fuso na resposta é UTC.** O Laravel serializa em `...Z` o que passou pelo cast
 * do Eloquent, mas as telas que leem colunas cruas (`$timestamps = false`, um `select` de coluna
 * que não é `created_at`) recebem `'2026-09-17 15:04:11'` — e aí o `new Date()` do V8 interpreta
 * como hora **local**. O próprio backend trata essas colunas como UTC
 * (`Carbon::createFromFormat(..., 'America/Sao_Paulo')->setTimezone('UTC')` nos filtros de
 * período), então a regra é uma só e mora aqui.
 *
 * Era isso que fazia o painel mostrar **o mesmo instante com 3 horas de diferença em duas telas**:
 * a Base de Leads concatenava um `'Z'` na string à mão e a Entrega de Trabalho não.
 */
const parseApiInstant = (date: string): Date => {
  const naive = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(date)

  return new Date(naive ? `${date.replace(' ', 'T')}Z` : date)
}

/**
 * Formata um instante como `'DD/MM/AAAA HH:MM'` (com os segundos, se pedidos).
 *
 * Sem locale, o navegador decidia o formato: em en-US saía `9/17/2026 3:04:11 PM`, e o
 * `slice(0, -3)` que existia aqui cortava o `' PM'` e **deixava os segundos**. O corte por
 * posição é o que quebra quando o formato muda, então o que não se quer mostrar não entra
 * nas opções do `Intl` em vez de ser removido depois.
 *
 * Um valor vazio ou inválido sai como `''`, a mesma convenção do `formatApiDate` — antes a
 * tela recebia `'Invalid Date Invalid Date'`.
 */
export const formatDateTime = (date?: string | null, opts: { showSeconds?: boolean } = { showSeconds: false }) => {
  if (!date) return ''

  const d = parseApiInstant(date)
  if (Number.isNaN(d.getTime())) return ''

  const day = d.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit' })
  const time = d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    ...(opts.showSeconds ? { second: '2-digit' as const } : {})
  })

  return `${day} ${time}`
}

/**
 * Formata como `'DD/MM/AAAA HH:MM'` um horário **de parede**: sem fuso, já em America/Sao_Paulo.
 *
 * É o que os casts `datetime:Y-m-d\TH:i:s` da API devolvem (o `scheduled_at` dos posts do blog
 * e dos reajustes de preço, por exemplo) — o mesmo valor que o `<input type="datetime-local">`
 * lê e grava. O `formatDateTime` trata data sem fuso como UTC e mostraria 3 horas a menos, então
 * aqui não há `Date` envolvido.
 */
export const formatLocalDateTime = (date?: string | null): string => {
  if (!date) return ''

  const [datePart, timePart = ''] = date.replace(' ', 'T').split('T')

  return `${formatIsoDate(datePart)} ${timePart.slice(0, 5)}`.trim()
}

/**
 * Data no formato do `<input type="datetime-local">` (`'AAAA-MM-DDTHH:MM'`), no fuso do navegador.
 * Sem argumento, é o agora — o `min` dos campos de agendamento.
 */
export const toDateTimeLocalValue = (date: Date = new Date()): string => {
  const copy = new Date(date)
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset())

  return copy.toISOString().slice(0, 16)
}

/**
 * Agora como horário de parede de America/Sao_Paulo, no formato do `<input type="datetime-local">`.
 *
 * Para comparar com os horários sem fuso da API (os casts `datetime:Y-m-d\TH:i:s`), que já estão
 * nesse fuso: o `toDateTimeLocalValue` usa o fuso do navegador, que fora de Brasília (Manaus, Acre,
 * uma VM em UTC) daria horas de diferença.
 */
export const nowSaoPauloDateTimeLocal = (): string => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
      .formatToParts(new Date())
      .map((part) => [part.type, part.value])
  )

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

export const capitalize = (str: string | null | undefined) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Tamanho de arquivo em pt-BR, arredondado para cima do KB.
 *
 * Havia duas cópias idênticas disto, uma na entrega de trabalho e outra no aproveitamento de
 * estudos — as duas telas que listam os arquivos escolhidos antes de enviar.
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 KB'
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

export const formatNumber = (value: string, decimalPlaces = 2): string => {
  // Remove tudo que não é dígito ou vírgula
  let sanitized = value.replace(/[^\d,]/g, '')

  // Se houver mais de uma vírgula, mantém só a primeira
  const firstComma = sanitized.indexOf(',')
  if (firstComma !== -1) {
    sanitized = sanitized.slice(0, firstComma + 1) + sanitized.slice(firstComma + 1).replace(/,/g, '')
  }

  // Se terminar com vírgula, retorna mantendo a vírgula
  // (já garantido que só há uma vírgula)
  const parts = sanitized.split(',')
  let integerPart = parts[0]
  let decimalPart = parts[1] || ''

  // Limita a quantidade de casas decimais
  decimalPart = decimalPart.slice(0, decimalPlaces)

  // Formata parte inteira com pontos
  integerPart = integerPart.replace(/^0+/, '') || '0' // remove zeros à esquerda, mas mantém pelo menos um zero
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

  // Monta resultado
  let result = integerPart
  if (sanitized.endsWith(',')) {
    result += ','
  } else if (decimalPart) {
    result += ',' + decimalPart
  }

  return result
}

/**
 * Implements the PMT financial function (equivalent to Excel/Google Sheets PGTO).
 * Returns the periodic payment using the same sign convention as `pv`
 * (positive for positive present values, negative for negative present values).
 *
 * @param rate  Interest rate per period (e.g., 0.0349 for 3.49%)
 * @param nper  Total number of payment periods
 * @param pv    Present value (price with discount)
 */
export const calculatePmt = (rate: number, nper: number, pv: number): number => {
  if (!Number.isFinite(rate) || !Number.isFinite(nper) || !Number.isFinite(pv) || nper <= 0) {
    return 0
  }

  if (rate === 0) return pv / nper

  const factor = Math.pow(1 + rate, nper)
  const denominator = factor - 1

  if (!Number.isFinite(factor) || !Number.isFinite(denominator) || denominator === 0) {
    return 0
  }

  const payment = (pv * rate * factor) / denominator

  return Number.isFinite(payment) ? payment : 0
}

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const brlValueFormatter = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Aceita as duas formas de número que chegam aqui e devolve 0 para o resto.
 *
 * A API responde decimal como string no formato do banco (`'1234.50'`), com ponto decimal e
 * sem separador de milhar — é o que `starting_bid`, `current_bid` e `preco` trazem. Mas um
 * valor já formatado em pt-BR (`'1.234,50'`) também pode cair aqui, e aí o ponto é milhar.
 *
 * A vírgula é o que separa os dois casos: **só existe milhar onde existe vírgula decimal**.
 * Tirar os pontos sem checar isso transformaria `'1234.50'` em `123450` — cem vezes o valor,
 * silenciosamente, em cima de dinheiro de verdade.
 */
const toFiniteNumber = (value: number | string): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  const raw = String(value)
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw
  const num = Number(normalized)

  return Number.isFinite(num) ? num : 0
}

/**
 * `1234.5` -> `'R$ 1.234,50'`. O padrão para exibir dinheiro no painel.
 *
 * Havia 17 formatadores locais para isto, em duas variantes que não dá para trocar uma pela
 * outra: com e sem o `R$` embutido. Esta é a com — a de baixo é a sem.
 */
export const formatCurrency = (value: number | string): string => brlFormatter.format(toFiniteNumber(value))

/**
 * Como `formatCurrency`, mas `0` vira `'Grátis'` (ABRA-399): o valor de uma venda, ou de um item, de
 * produto gratuito. Só para onde o zero quer dizer isso — um total, um saldo ou um repasse zerados
 * continuam em `formatCurrency`.
 */
export const formatCurrencyOrFree = (value: number | string): string =>
  toFiniteNumber(value) === 0 ? 'Grátis' : formatCurrency(value)

/**
 * `1234.5` -> `'1.234,50'`, sem o símbolo.
 *
 * Só para onde o `R$` já é renderizado à parte: um `InputLeftAddon`, um rótulo, uma coluna
 * cujo cabeçalho já diz "Valor (R$)". Em qualquer outro lugar use `formatCurrency`.
 */
export const formatCurrencyValue = (value: number | string): string => brlValueFormatter.format(toFiniteNumber(value))

/**
 * `1234567` -> `'R$ 1,2M'`. Para eixo de gráfico e cartão de resumo, onde o valor exato não
 * cabe. O separador decimal é a vírgula: as duas cópias locais usavam `toFixed`, que devolve
 * ponto, e escreviam `R$ 1.2M` no meio de uma tela em pt-BR.
 */
export const formatCompactCurrency = (value: number): string => {
  const abs = Math.abs(value)

  if (abs >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1).replace('.', ',')}M`
  if (abs >= 1_000) return `R$ ${(value / 1_000).toFixed(0)}K`

  return `R$ ${value.toFixed(0)}`
}

/**
 * Retorna se o usuário tem a 'role' especificada
 */
export function useHasRole() {
  const { roles } = useUser()

  return useCallback(
    (roleSlug: string | string[]) => {
      if (Array.isArray(roleSlug)) {
        return roleSlug.some((slug) => roles.includes(slug))
      }
      return roles.includes(roleSlug)
    },
    [roles]
  )
}

/**
 * Formata número de telefone baseado no tamanho da string
 * Exemplos:
 * - (99) 9999-9999 (telefone fixo - 10 dígitos)
 * - (99) 99999-9999 (celular - 11 dígitos)
 * - +55 (99) 9999-9999 (fixo com o DDI do Brasil - 12 dígitos)
 * - +99 (99) 99999-9999 (celular com código de país 2 dígitos - 13 dígitos)
 * - +999 (99) 99999-9999 (celular com código de país 3 dígitos - 14 dígitos)
 *
 * **Aceita vazio e devolve `''`**, a mesma convenção do `formatApiDate` e do `formatDateTime`.
 * A tipagem dizia `string`, mas o registro que ainda não chegou da API entrega `undefined` — e
 * um `<FormModal>` de leitura fica **montado** enquanto o fetch corre (é o que o eixo dos modais
 * decidiu), então o `value.replace` de um formatador estrito derruba a página inteira, não só a
 * célula. Um formatador de exibição não é lugar para exigir valor.
 *
 * **Número com código de outro país sai como veio** (ABRA-438), só sem caracteres que não são
 * de telefone. As máscaras daqui são brasileiras, e encaixar um DDD em `+1 (415) 555-2671` — o
 * formato que os forms do site gravam — dava `(14) 15555-2671`. Vale também para quem digita no
 * `MaskedInput`: começar com `+` deixa digitar um número estrangeiro sem a máscara brasileira, e
 * um número brasileiro que passe de 11 dígitos sem o 55 na frente fica como foi digitado.
 */
export const formatPhone = (value?: string | null): string => {
  const trimmed = (value ?? '').trim()
  if (trimmed.startsWith('+') && !trimmed.startsWith('+55')) {
    return trimmed.replace(/[^\d+\s()-]/g, '')
  }

  const numbers = trimmed.replace(/\D/g, '')
  if (numbers.length === 0) return ''

  // Até 2 dígitos: (99
  if (numbers.length <= 2) {
    return `(${numbers}`
  }
  // 3-6 dígitos: (99) 999
  else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
  }
  // 7-10 dígitos: (99) 9999-9999 (telefone fixo)
  else if (numbers.length <= 10) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`
  }
  // 11 dígitos: (99) 99999-9999 (celular)
  else if (numbers.length === 11) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`
  }
  // 12 dígitos com 55 na frente: fixo com DDI, como a API grava ("+55 11 3881-1200")
  else if (numbers.length === 12 && numbers.startsWith('55')) {
    return `+55 (${numbers.slice(2, 4)}) ${numbers.slice(4, 8)}-${numbers.slice(8, 12)}`
  }
  // 12 dígitos: +99 (9... (ainda digitando)
  else if (numbers.length === 12) {
    return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9, 12)}`
  }
  // 13 dígitos: +99 (99) 99999-9999
  else if (numbers.length === 13) {
    return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9, 13)}`
  }
  // 14+ dígitos: +999 (99) 99999-9999
  else {
    return `+${numbers.slice(0, 3)} (${numbers.slice(3, 5)}) ${numbers.slice(5, 10)}-${numbers.slice(10, 14)}`
  }
}

/**
 * `'01310100'` -> `'01310-100'`. Máscara de CEP.
 *
 * Os dois campos de CEP do painel não tinham máscara nenhuma — só um `maxLength={9}` e um
 * `placeholder="00000-000"` que descrevia um formato que o campo não produzia.
 */
export const formatZipCode = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 8)
  if (numbers.length <= 5) return numbers

  return `${numbers.slice(0, 5)}-${numbers.slice(5)}`
}

/**
 * Formata CPF e CNPJ baseado no tamanho da string
 * CPF: 000.000.000-00
 * CNPJ: 00.000.000/0000-00
 */
export const formatDocument = (value?: string | null, onlyCpf = false): string => {
  // Removes all non-digits characters — vazio devolve `''`, como o `formatPhone`.
  const numbers = (value ?? '').replace(/\D/g, '')
  if (numbers.length === 0) return ''

  // Only CPF
  if (onlyCpf) {
    return numbers
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }

  // CPF
  if (numbers.length <= 11) {
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }

  // CNPJ
  return numbers
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

/**
 * Formata chave aleatória de pix (formato 8-4-4-4-12)
 *
 * Exemplo: 'aOaJqwj48F612A5S8qwe1A5s6arb12Pq' → "aOaJqwj4-8F61-2A5S-8qwe-1A5s6arb12Pq"
 */
export const formatRandomPixKey = (value: string): string => {
  // Remove tudo que não é alfanumérico
  const clean = value.replace(/[^a-zA-Z0-9]/g, '')

  if (clean.length === 0) return ''

  // Formato: 8-4-4-4-12
  let formatted = ''

  if (clean.length <= 8) {
    formatted = clean
  } else if (clean.length <= 12) {
    formatted = `${clean.slice(0, 8)}-${clean.slice(8)}`
  } else if (clean.length <= 16) {
    formatted = `${clean.slice(0, 8)}-${clean.slice(8, 12)}-${clean.slice(12)}`
  } else if (clean.length <= 20) {
    formatted = `${clean.slice(0, 8)}-${clean.slice(8, 12)}-${clean.slice(12, 16)}-${clean.slice(16)}`
  } else {
    formatted = `${clean.slice(0, 8)}-${clean.slice(8, 12)}-${clean.slice(12, 16)}-${clean.slice(16, 20)}-${clean.slice(
      20,
      32
    )}`
  }

  return formatted
}

/**
 * Remove accents of a string (ex: "João" → "Joao")
 *
 * @param str The string to remove accents from
 * @returns The string without accents
 */
export const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Letters that carry no combining accent, so `removeAccents` cannot reduce them.
 * Only the ones a pt-BR keyboard can realistically produce are listed.
 */
const SLUG_TRANSLITERATIONS: Record<string, string> = {
  æ: 'ae',
  œ: 'oe',
  ø: 'o',
  ð: 'd',
  þ: 'th',
  ß: 'ss',
  đ: 'd',
  ł: 'l'
}

/**
 * Character class built from the map above, so the two can never drift apart — a letter
 * listed in only one of them would put the literal string "undefined" in the slug.
 * None of the keys are regex metacharacters, so they go into the class as they are.
 */
const SLUG_TRANSLITERABLE = new RegExp(`[${Object.keys(SLUG_TRANSLITERATIONS).join('')}]`, 'g')

/**
 * Normalizes a URL slug as the user types: accents are transliterated
 * ("concurso-de-piáção" → "concurso-de-piacao") and anything the API's
 * `alpha_dash:ascii` rule rejects becomes a hyphen.
 *
 * Trailing hyphens survive on purpose — stripping them would make it impossible
 * to type a hyphen between two words.
 *
 * @param value The raw field value
 * @returns The value reduced to lowercase letters, digits, `-` and `_`
 */
export const slugify = (value: string): string =>
  removeAccents(value.toLowerCase())
    .replace(SLUG_TRANSLITERABLE, (char) => SLUG_TRANSLITERATIONS[char])
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')

/** O que d\u00e1 para extrair com seguran\u00e7a de um erro do axios, sem recorrer a `any`. */
export type ApiErrorShape = {
  response?: { status?: number; data?: { errors?: Record<string, string | string[]> } }
}

/**
 * Erros de campo da API no formato do estado do formul\u00e1rio: uma string por campo, aplicada
 * sobre `base` para que os campos que a API n\u00e3o citou voltem a ficar vazios em vez de sumirem
 * do objeto. A API manda um array por campo, que ia cru para um estado tipado como string.
 *
 * @param base O objeto de erros vazio do formul\u00e1rio, que define os campos conhecidos
 * @param errors O `errors` do corpo da resposta, quando houver
 */
export const toFormErrors = <T extends Record<string, string>>(
  base: T,
  errors?: Record<string, string | string[]>
): T => {
  const normalised: Record<string, string> = { ...base }

  Object.entries(errors ?? {}).forEach(([field, messages]) => {
    normalised[field] = Array.isArray(messages) ? messages.join(' ') : messages
  })

  return normalised as T
}

/**
 * Base pública do site da ABRA e base do próprio painel, sem a barra final.
 *
 * O `.replace` não é detalhe: o `.env.example` já traz o `REACT_APP_API_URL` terminando em `/`, e
 * basta alguém fazer o mesmo com as outras duas para todo link montado por interpolação sair com
 * `//`. Era exatamente o que acontecia: `frontendUrl` estava derivado à mão em duas fichas, uma
 * **com** o `.replace` (`ContestDetailPage`) e outra **sem** (`EventDetailPage`), e o
 * `REACT_APP_WEBSITE_URL` era relido em mais dois lugares (`ContestsPage`, `Routers`) apesar de o
 * `WEBSITE_URL` já existir aqui. As duas constantes moram juntas para as cinco cópias não voltarem.
 */
export const WEBSITE_URL = (process.env.REACT_APP_WEBSITE_URL || 'https://abra.com.br').replace(/\/$/, '')

export const FRONTEND_URL = (process.env.REACT_APP_FRONTEND_URL || 'https://painel.abra.com.br').replace(/\/$/, '')

/**
 * Link público do concurso no site. O prefixo vem do `link_type` escolhido no cadastro (ABRA-344):
 * `/concurso/{slug}` ou `/exposicao/{slug}`. Registros sem o campo caem em `concurso`.
 */
export const contestPublicUrl = (contest: { slug: string; link_type?: TContestLinkType }) =>
  `${WEBSITE_URL}/${contest.link_type ?? 'concurso'}/${contest.slug}`

/**
 * Builds a preview URL for any entity that has a slug and id.
 *
 * @param websiteUrl Base URL of the website (without trailing slash)
 * @param basePath Path segment that precedes the slug (e.g. `/eventos`)
 * @param item Object containing the entity slug and id
 * @returns Formatted URL with the preview token
 */
export function buildPreviewUrl(
  websiteUrl: string,
  basePath: string,
  item: { slug: string; id: number | string }
): string {
  const token = encodeURIComponent(btoa(String(item.id)))
  return `${websiteUrl}${basePath}/${item.slug}?preview=${token}`
}

export function buildEventPreviewUrl(websiteUrl: string, event: Pick<Event, 'slug' | 'id'>): string {
  return buildPreviewUrl(websiteUrl, '/eventos', event)
}

/**
 * Checks if an event has already ended based on its date and time fields.
 * Uses substring slicing to handle both 'YYYY-MM-DD' and full ISO strings
 * (e.g. '2025-01-15T00:00:00.000000Z') that Laravel may return for date casts.
 */
export function isEventPast(event: Pick<Event, 'date_start' | 'date_end' | 'time_start' | 'time_end'>): boolean {
  const rawDate = event.date_end ?? event.date_start
  const rawTime = event.time_end ?? event.time_start ?? '23:59'
  const datePart = rawDate.substring(0, 10) // always 'YYYY-MM-DD'
  const timePart = rawTime.substring(0, 5) // always 'HH:mm'
  const eventEnd = new Date(`${datePart}T${timePart}`)
  return !isNaN(eventEnd.getTime()) && eventEnd < new Date()
}

export function buildTeacherPreviewUrl(websiteUrl: string, teacher: Pick<Teacher, 'slug' | 'id'>): string {
  return buildPreviewUrl(websiteUrl, '/nosso-ensino/professores', teacher)
}

export function buildAsaasPaymentUrl(paymentId: string): string {
  return `https://www.asaas.com/payment/show/${paymentId}`
}

/**
 * Capitalizes a name with proper Portuguese title case formatting
 * Respects common exceptions like 'da', 'de', 'do', 'das', 'dos', 'e'
 *
 * @param name The name string to capitalize
 * @returns The capitalized name with proper formatting
 */
export const capitalizeName = (name: string): string => {
  if (!name) return ''

  const exceptions = ['da', 'de', 'do', 'das', 'dos', 'e']

  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word, index) => {
      if (index > 0 && exceptions.includes(word)) {
        return word
      }
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

/** Que tipo de chave Pix foi cadastrada — `PIX_KEY_MASKS` diz qual máscara cada um usa. */
export type PixKeyType = 'cpf_cnpj' | 'email' | 'phone' | 'random'
